import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Check,
} from "lucide-react";

const AnnouncementsPage = () => {
  const { sessionToken } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchAnnouncements = useCallback(async () => {
    if (!sessionToken) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/announcements`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  const fetchUnreadCount = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await axios.get(`${API}/announcements/unread/count`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchAnnouncements();
    fetchUnreadCount();
  }, [fetchAnnouncements, fetchUnreadCount]);

  const markAsRead = async (announcementId) => {
    try {
      await axios.post(`${API}/announcements/${announcementId}/mark-read`, null, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      // Refresh counts
      fetchUnreadCount();
      // Update local state
      setAnnouncements(prev => prev.map(a => 
        a.announcement_id === announcementId 
          ? { ...a, read_by: [...(a.read_by || []), "current_user"] }
          : a
      ));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "urgent":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "warning":
        return <Badge className="bg-amber-100 text-amber-700">Warning</Badge>;
      case "success":
        return <Badge className="bg-green-100 text-green-700">Success</Badge>;
      case "urgent":
        return <Badge className="bg-red-100 text-red-700">Urgent</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700">Info</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="announcements-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-500" />
            Announcements
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Stay updated with the latest platform news and updates
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-red-500 text-white">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No announcements at this time</p>
            <p className="text-sm text-slate-400 mt-1">Check back later for updates</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => {
            const isRead = announcement.read_by?.includes("current_user");
            
            return (
              <Card 
                key={announcement.announcement_id}
                className={`transition-all ${!isRead ? "border-l-4 border-l-blue-500" : ""}`}
                data-testid={`announcement-${announcement.announcement_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getTypeIcon(announcement.type)}
                      <div>
                        <CardTitle className="text-lg">{announcement.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {getTypeBadge(announcement.type)}
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(announcement.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!isRead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(announcement.announcement_id)}
                        className="text-slate-500"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Mark read
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className="prose prose-sm max-w-none text-slate-600"
                    dangerouslySetInnerHTML={{ __html: announcement.content }}
                  />
                  {announcement.expires_at && (
                    <p className="text-xs text-slate-400 mt-4">
                      Expires: {formatDate(announcement.expires_at)}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
